import styles from './DirectPurchaseModal.module.css';

import type { FC } from 'react';
import type { ChainKey } from 'src/blockchain';
import type { ApiListingItem } from 'src/types/listing';
import type { PurchaseChainOption } from 'src/utils/purchaseChains';

import React, { useMemo, useState } from 'react';
import { Car, Warning, Spinner, CheckCircle } from '@phosphor-icons/react';

import { useUserSession } from 'src/hooks/useUserSession';

import axios from 'src/utils/axios';
import { formatAmount } from 'src/utils/money';
import { withWalletTimeout } from 'src/utils/walletTimeout';
import {
  explorerTxLink,
  listingChainsOrLegacy,
  resolvePurchaseChainOptions,
} from 'src/utils/purchaseChains';
import {
  getApiErrorMessage,
  isListingUnavailable,
  describeUnavailability,
} from 'src/utils/listingAvailability';

import { useBuyerChains } from 'src/queries/useBuyerChains';
import { useWallet, useWalletUI, WalletButton } from 'src/blockchain';
import { useDirectPurchase, useDirectPurchaseStatus } from 'src/queries/useDirectPurchase';

import { Modal } from 'src/components/Modal';
import { Stack } from 'src/components/themed/Stack';
import { Typography } from 'src/components/themed/Typography';
import { NetworkSelector } from 'src/components/NetworkSelector';

import { settledChain } from './settledChain';
import { settlementAmount } from './settlementAmount';
import { walletConnectPromptFamily } from './walletConnectPrompt';

interface DirectPurchaseModalProps {
  visible: boolean;
  onClose: () => void;
  listing: ApiListingItem;
}

// The server verifies the referenced tx asynchronously and stores a technical
// fail reason; translate the known ones into buyer-facing copy.
const FAIL_REASON_COPY: Array<[substring: string, copy: string]> = [
  ['execution failed on-chain', 'The transaction failed on-chain (rejected by the network).'],
  ['no Transfer event', 'The transaction does not contain a transfer of the settlement token.'],
  ['recipient does not match', "The payment went to a different wallet than the seller's."],
  ['sender does not match', 'The payment was sent from a different wallet than yours.'],
  ['below the listing price', 'The transferred amount is below the listing price.'],
  ['timeout', 'The transaction was not found on-chain within 15 minutes.'],
];

const describeFailReason = (reason: string | null): string => {
  const match = reason
    ? FAIL_REASON_COPY.find(([substring]) => reason.includes(substring))
    : undefined;

  return match?.[1] ?? reason ?? 'The payment could not be verified.';
};

type Phase = 'form' | 'verifying' | 'verified' | 'failed';

export const DirectPurchaseModal: FC<DirectPurchaseModalProps> = ({
  visible,
  onClose,
  listing,
}) => {
  const { account } = useUserSession();
  const { data: buyerChains, isLoading: isLoadingChains } = useBuyerChains();
  const directPurchaseMutation = useDirectPurchase();

  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string>('');
  const [txHash, setTxHash] = useState<string>('');
  const [purchaseId, setPurchaseId] = useState<string | null>(null);
  const [chosenChain, setChosenChain] = useState<ChainKey | null>(null);
  // The network the money actually went to, frozen at confirmation. Everything after the payment
  // reads from here rather than from `options`, which is live: `/buyer/chains` refetches while the
  // modal sits in "verifying", and a chain that disappears or reorders would otherwise silently
  // re-point the explorer link and the token symbol at a different network than the one paid on.
  const [paidOption, setPaidOption] = useState<PurchaseChainOption | null>(null);

  // Networks this buyer may pay this listing on (spec §4.3). Every factor of the intersection is
  // owned by whoever can be trusted with it — see resolvePurchaseChainOptions.
  const options = useMemo(
    () =>
      resolvePurchaseChainOptions({
        buyerChains,
        listingChains: listingChainsOrLegacy({
          listingChains: listing.availableChains,
          buyerChains,
          sellerAddress: listing.seller?.walletAddress,
          sellerFamily: listing.seller?.chainType,
        }),
        listingCurrency: listing.currency,
        linkedFamilies: account?.wallets?.map((wallet) => wallet.chainType),
      }),
    [account?.wallets, buyerChains, listing.availableChains, listing.currency, listing.seller]
  );

  // Nothing is preselected in state until the buyer picks: with one network there is no choice to
  // make, and with several the first is a sane default that a click replaces.
  const selected: PurchaseChainOption | undefined =
    options.find((option) => option.chainKey === chosenChain) ?? options[0];

  // Both wallet hooks run every render; the family only decides which result is used, so hook
  // order never depends on the selected network (blockchain/hooks.ts).
  const { wallet } = useWallet(selected?.family);
  const { transferToken } = useWalletUI(selected?.family);

  // The wallet that must pay: the account's linked wallet of the selected family. Paying from any
  // other connected wallet still moves the money but fails verification afterwards — the server
  // checks the sender — so it is refused here, before the transfer.
  //
  // When api does not report the account's wallets (a deployment predating the field), there is
  // nothing to compare against: fall back to the pre-multichain rule, "a connected wallet of this
  // family will do". The purchase gate already guarantees that wallet belongs to the account.
  const linkedAddress = account?.wallets?.find(
    (linked) => linked.chainType === selected?.family
  )?.walletAddress;
  const connectedAddress = wallet?.address;
  const isLinkedWalletConnected =
    !!connectedAddress &&
    (account?.wallets === undefined
      ? true
      : !!linkedAddress && connectedAddress.toLowerCase() === linkedAddress.toLowerCase());

  // The one place the app can offer to connect the wallet the selected network needs.
  const promptFamily = walletConnectPromptFamily(selected, isLinkedWalletConnected);

  // What the buyer pays: quoted by the server next to the token and the payout address, because
  // the app has no business computing money (see settlementAmount).
  const amount = settlementAmount(selected, listing.price);

  const handleClose = () => {
    // Block closing only while the wallet prompt is in flight (bounded by the wallet
    // timeout) — closing mid-signature would desync the UI from the wallet.
    if (isProcessing) {
      return;
    }
    setError('');
    setTxHash('');
    setPurchaseId(null);
    setPaidOption(null);
    onClose();
  };

  const handleConfirm = async () => {
    setIsProcessing(true);
    setError('');

    try {
      if (!selected) {
        throw new Error('No network is available for this purchase.');
      }

      if (!isLinkedWalletConnected) {
        throw new Error(
          `Connect the ${selected.displayName} wallet linked to your account to pay on this network.`
        );
      }

      // Pre-flight availability check BEFORE the wallet prompt: the transfer is irreversible
      // and happens client-side, so if someone bought this car while the page was open (the
      // "third buyer" scenario), we must stop before money moves.
      const fresh = await axios.get<ApiListingItem>(`/buyer/listings/${listing.id}`);
      const availability = fresh.data.availability;

      if (isListingUnavailable(availability)) {
        throw new Error(
          `${describeUnavailability(availability).description} Your wallet was not charged.`
        );
      }

      // Direct on-chain transfer to the seller on the chosen network (no escrow). Token address,
      // decimals and the payout address all come from api — the chain id switches an EVM wallet
      // to the deal's network before the transaction and is ignored on TRON.
      const result = await withWalletTimeout(
        transferToken({
          tokenAddress: selected.token.address,
          to: selected.sellerPayoutAddress,
          amount: String(amount),
          decimals: selected.token.decimals,
          chainId: selected.chainId,
        })
      );

      setTxHash(result.hash);
      setPaidOption(selected);

      // Record the purchase. Backend derives seller/amount/token from the listing and the chain,
      // and the buyer from the authenticated wallet, then verifies the tx on-chain before
      // notifying the seller.
      const created = await directPurchaseMutation.mutateAsync({
        listingId: listing.id,
        txHash: result.hash,
        chainKey: selected.chainKey,
      });

      setPurchaseId(created.id);
    } catch (err) {
      // The API rejects with the error body (not an Error), e.g. the availability
      // guard's "Listing is no longer available for direct purchase".
      setError(getApiErrorMessage(err, 'Transaction failed. Please try again.'));
    } finally {
      setIsProcessing(false);
    }
  };

  // Polls pending → verified | failed; the server reaches a terminal status on its
  // own (worker every 30s, hard timeout 15 min), so closing the modal is safe.
  const statusQuery = useDirectPurchaseStatus(purchaseId);
  const purchase = statusQuery.data;

  const phase: Phase =
    purchaseId == null
      ? 'form'
      : purchase?.status === 'verified'
        ? 'verified'
        : purchase?.status === 'failed'
          ? 'failed'
          : 'verifying';

  const icon =
    phase === 'verified' ? (
      <CheckCircle size={64} color="white" weight="fill" />
    ) : phase === 'failed' ? (
      <Warning size={64} color="white" weight="fill" />
    ) : phase === 'verifying' ? (
      <Spinner size={64} color="white" className={styles.spinner} />
    ) : (
      <Car size={64} color="white" weight="fill" />
    );

  const header =
    phase === 'verified'
      ? 'Payment confirmed'
      : phase === 'failed'
        ? 'Payment not confirmed'
        : phase === 'verifying'
          ? 'Verifying payment'
          : `Buy now: ${listing.make.name} ${listing.model.name}`;

  // No network to pay on: either the seller cannot receive anywhere this buyer may pay, or the
  // buyer has linked no wallet of a usable family. Say so instead of offering a dead button.
  const canPay = !!selected && isLinkedWalletConnected;

  return (
    <Modal
      visible={visible}
      onClose={handleClose}
      icon={icon}
      header={header}
      buttonText={phase === 'form' ? (isProcessing ? 'Processing…' : 'Confirm purchase') : 'Close'}
      onSubmit={phase === 'form' ? handleConfirm : handleClose}
      disabled={isProcessing || (phase === 'form' && !canPay)}
    >
      <Stack gap={3} width="100%">
        {phase === 'form' ? (
          <>
            {isLoadingChains ? null : (
              <NetworkSelector
                options={options}
                selected={selected?.chainKey ?? null}
                onSelect={setChosenChain}
              />
            )}

            <Stack gap={1} className={styles.info}>
              <Row
                label="Vehicle"
                value={`${listing.make.name} ${listing.model.name} ${listing.year}`}
              />
              <Row
                label="Amount"
                value={`${formatAmount(amount, 6)} ${settledChain(paidOption, selected)?.token.symbol ?? listing.currency}`}
              />
              <Row label="Seller wallet" value={selected?.sellerPayoutAddress ?? '—'} mono />
            </Stack>

            {!isLoadingChains && options.length === 0 ? (
              <Stack gap={1} className={styles.error}>
                <Typography color="white" fontWeight="600">
                  No network available for this purchase.
                </Typography>
                <Typography color="lightWhite" variant="small">
                  Link a wallet of a network this seller accepts, then try again.
                </Typography>
              </Stack>
            ) : null}

            {selected && promptFamily ? (
              <Stack gap={1} className={styles.error}>
                <Typography color="white" variant="small">
                  Connect the {selected.displayName} wallet linked to your account
                  {linkedAddress ? ` (${linkedAddress})` : ''} to pay on this network.
                </Typography>
                {/* The connect control belongs next to the demand, because nowhere else offers it
                    for the family being paid on — see walletConnectPrompt.ts. When a wallet of this
                    family is connected but is not the linked one, the button shows that address and
                    disconnects it, which is the way back to the right wallet. */}
                <WalletButton family={promptFamily} />
              </Stack>
            ) : null}

            <Stack gap={1} className={styles.warning}>
              <Stack flexDirection="row" gap={1} alignItems="center">
                <Warning size={20} color="white" weight="fill" />
                <Typography color="white" fontWeight="600">
                  No escrow protection
                </Typography>
              </Stack>
              <Typography color="lightWhite" variant="small">
                Funds are transferred directly to the seller and the transaction is irreversible.
                Only proceed if you are at the dealership and ready to take the vehicle.
              </Typography>
            </Stack>
          </>
        ) : phase === 'verifying' ? (
          <Stack gap={1} className={styles.info}>
            <Typography color="white" fontWeight="600">
              Verifying your payment on-chain…
            </Typography>
            <Typography color="lightWhite" variant="small">
              This usually takes under a minute. You can close this window — verification continues
              on the server and the seller will be notified automatically once the payment is
              confirmed.
            </Typography>
            <TxLine txHash={txHash} option={settledChain(paidOption, selected)} />
          </Stack>
        ) : phase === 'verified' ? (
          <Stack gap={1} className={styles.info}>
            <Typography color="white" fontWeight="600">
              Payment confirmed. The seller has been notified.
            </Typography>
            <TxLine txHash={txHash} option={settledChain(paidOption, selected)} />
          </Stack>
        ) : (
          <Stack gap={1} className={styles.error}>
            <Typography color="white" fontWeight="600">
              {describeFailReason(purchase?.failReason ?? null)}
            </Typography>
            <Typography color="lightWhite" variant="small">
              Please contact the dealer on site to resolve the payment.
            </Typography>
            <TxLine txHash={txHash} option={settledChain(paidOption, selected)} />
          </Stack>
        )}

        {error && phase === 'form' ? (
          <Stack gap={1} className={styles.error}>
            <Typography color="white" variant="small">
              {error}
            </Typography>
            {txHash ? (
              // The transfer already went out but the purchase was rejected (lost a race
              // for the car). Direct sales are resolved at the dealership — point there.
              <Typography color="lightWhite" variant="small">
                Your payment was sent (Tx: <span className={styles.mono}>{txHash}</span>). Please
                contact the dealer on site to resolve it.
              </Typography>
            ) : null}
          </Stack>
        ) : null}
      </Stack>
    </Modal>
  );
};

/**
 * The transaction, linked to the explorer of the network it was **actually** sent on — the option
 * frozen at confirmation, never the live selection.
 */
const TxLine: FC<{ txHash: string; option: PurchaseChainOption | null }> = ({ txHash, option }) => {
  if (!txHash) {
    return null;
  }

  return (
    <Typography color="lightWhite" variant="small" className={styles.mono}>
      Tx:{' '}
      {option ? (
        <a href={explorerTxLink(option, txHash)} target="_blank" rel="noreferrer">
          {txHash}
        </a>
      ) : (
        txHash
      )}
    </Typography>
  );
};

const Row: FC<{ label: string; value: string; mono?: boolean }> = ({ label, value, mono }) => (
  <Stack flexDirection="row" justifyContent="space-between" gap={2}>
    <Typography color="lightWhite" variant="small">
      {label}
    </Typography>
    <Typography color="white" variant="small" className={mono ? styles.mono : undefined}>
      {value}
    </Typography>
  </Stack>
);
